import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(valor: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor);
}

export function parseCurrency(formatted: string): number {
  // Remove R$, espaços e pontos, troca vírgula por ponto
  const cleaned = formatted
    .replace(/R\$\s?/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  
  return parseFloat(cleaned) || 0;
}

export function formatDateBR(dateString: string): string {
  // Se já está no formato DD/MM/YYYY, retorna
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    return dateString;
  }
  
  // Se está no formato YYYY-MM-DD, converte
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }
  
  // Tenta parsear e formatar
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString('pt-BR');
  }
  
  return dateString;
}

export function formatPhone(phone: string | undefined | null): string {
  if (!phone) return '';
  
  // Remove tudo que não é número
  let cleaned = phone.replace(/\D/g, '');
  
  // Remove o 55 do início se existir (código do Brasil)
  if (cleaned.startsWith('55') && cleaned.length > 11) {
    cleaned = cleaned.slice(2);
  }
  
  // Se tem 11 dígitos (DDD + 9 dígitos): 71 99999-9999
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  
  // Se tem 10 dígitos (DDD + 8 dígitos): 71 9999-9999
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  
  // Se tem 9 dígitos (sem DDD, celular): retorna sem DDD
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
  }
  
  // Se tem 8 dígitos (sem DDD, fixo): retorna sem DDD
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  }
  
  // Retorna como está se não encaixar
  return phone;
}

/**
 * Máscara de telefone para inputs - formato brasileiro (XX) XXXXX-XXXX
 * Use esta função em onChange de inputs de telefone
 */
export function formatPhoneMask(value?: string | null): string {
  // Remove tudo que não é número
  const safeValue = value ?? '';
  let numbers = safeValue.replace(/\D/g, '');
  
  // Remove o código do país 55 se existir no início
  if (numbers.startsWith('55') && numbers.length > 11) {
    numbers = numbers.slice(2);
  }
  
  // Limita a 11 dígitos (DDD + 9 dígitos)
  const limited = numbers.slice(0, 11);
  
  // Aplica máscara (XX) XXXXX-XXXX
  if (limited.length <= 2) return limited;
  if (limited.length <= 7) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
  if (limited.length <= 11) return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
  return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
}

/**
 * Formata texto para Title Case (primeira letra de cada palavra maiúscula)
 * Converte "TEXTO MAIUSCULO" ou "texto minusculo" para "Texto Maiusculo"
 * Preserva acentos e caracteres especiais
 */
export function formatTitleCase(text: string): string {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (word.length === 0) return '';
      // Preserva palavras de conexão em minúsculo (opcional)
      const conectivos = ['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'para', 'por', 'com'];
      if (conectivos.includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ')
    // Garante que a primeira palavra sempre comece com maiúscula
    .replace(/^./, str => str.toUpperCase());
}

/**
 * Handler para usar em inputs que precisam de formatação Title Case
 * Formata automaticamente enquanto o usuário digita
 */
export function handleTitleCaseInput(
  e: React.ChangeEvent<HTMLInputElement>,
  setValue: (value: string) => void
): void {
  const formatted = formatTitleCase(e.target.value);
  setValue(formatted);
}

/**
 * Normaliza WhatsApp para formato brasileiro (11 dígitos: DDD + número)
 * Remove o código 55 se existir, mantém apenas DDD + número
 * Use esta função para armazenar e comparar números de WhatsApp
 */
export function normalizeWhatsappBR(value: string | undefined | null): string {
  if (!value) return '';
  
  // Remove tudo que não é número
  let digits = value.replace(/\D/g, '');
  
  // Se começar com 55 e tiver mais de 11 dígitos, remove o 55
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }
  
  // Retorna no máximo 11 dígitos
  return digits.slice(0, 11);
}

/**
 * Retorna número formatado para link wa.me (com código 55)
 * Entrada: qualquer formato de WhatsApp (com ou sem 55)
 * Saída: "5571999999999" (código país + DDD + número)
 * Use esta função sempre que abrir links wa.me
 */
export function toWhatsappLink(value: string | undefined | null): string {
  const normalized = normalizeWhatsappBR(value);
  if (normalized.length < 10) return ''; // Número inválido
  return `55${normalized}`;
}

/**
 * Abre WhatsApp com número e mensagem
 * - Desktop: usa web.whatsapp.com (evita bloqueio de api.whatsapp.com)
 * - Mobile: usa wa.me
 * Retorna true se abriu, false se número inválido
 */
export function openWhatsapp(phone: string | undefined | null, message: string = ''): boolean {
  const whatsappNumber = toWhatsappLink(phone);
  if (!whatsappNumber) return false;
  
  const encodedMessage = encodeURIComponent(message);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const url = isMobile
    ? `https://wa.me/${whatsappNumber}?text=${encodedMessage}`
    : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;
  
  window.open(url, '_blank');
  return true;
}

/**
 * Máscara de CPF para inputs - formato 000.000.000-00
 */
export function formatCpfMask(value: string): string {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
  if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
  return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
}

/**
 * Formata horário para exibição - HH:mm
 * Entrada: "07:01:16.658693" ou "07:01:16" ou "07:01"
 * Saída: "07:01" ou "+02:15" (se turno noturno e hora < 06:00)
 * 
 * @param time - Horário a formatar
 * @param options - Opções de formatação
 * @param options.isNightShift - Se true e hora < 06:00 e pedido é do dia anterior, adiciona prefixo "+"
 * @param options.pedidoData - Data do pedido (YYYY-MM-DD) para comparação em turno noturno
 * @param options.hoje - Data de hoje (YYYY-MM-DD) para comparação em turno noturno
 */
export function formatTime(
  time: string | undefined | null, 
  options?: { 
    isNightShift?: boolean;
    pedidoData?: string;
    hoje?: string;
  }
): string {
  if (!time) return '';
  
  // Pega apenas HH:mm (primeiros 5 caracteres)
  const match = time.match(/^(\d{2}):(\d{2})/);
  if (!match) return time;
  
  const formatted = `${match[1]}:${match[2]}`;
  const hora = parseInt(match[1], 10);
  
  // Se turno noturno ativo e horário é madrugada (00:00 a 05:59), adiciona "+"
  // Modo v2: se fornecidas datas, só adiciona + quando pedido é do dia anterior
  if (options?.isNightShift && hora >= 0 && hora < 6) {
    // Verificação com datas (mais precisa)
    if (options?.pedidoData && options?.hoje) {
      // Só adiciona + se o pedido é do dia anterior ao hoje
      if (options.pedidoData < options.hoje) {
        return `+${formatted}`;
      }
      // Pedido é de hoje mas com hora da madrugada = não precisa de +
      return formatted;
    }
    // Modo legado: sem datas, assume que sempre é turno continuado
    return `+${formatted}`;
  }
  
  return formatted;
}

/**
 * Gera código de cliente:
 * - Com telefone: Primeira letra + 4 últimos dígitos do telefone
 * - Sem telefone: Primeira letra + 4 primeiros dígitos do CPF
 */

export function gerarCodigoCliente(nome: string, whatsapp?: string, cpf?: string): string {
  const primeiraLetra = nome.trim().charAt(0).toUpperCase() || 'X';
  
  // Prioridade 1: WhatsApp (4 últimos dígitos)
  const telefoneDigitos = (whatsapp || '').replace(/\D/g, '');
  if (telefoneDigitos.length >= 4) {
    return `${primeiraLetra}${telefoneDigitos.slice(-4)}`;
  }
  
  // Prioridade 2: CPF (4 primeiros dígitos)
  const cpfDigitos = (cpf || '').replace(/\D/g, '');
  if (cpfDigitos.length >= 4) {
    return `${primeiraLetra}${cpfDigitos.slice(0, 4)}`;
  }
  
  // Fallback: 0000
  return `${primeiraLetra}0000`;
}

/**
 * Formata telefone mascarado para exibição segura
 * Exibe apenas os últimos 4 dígitos: (**) *****-1234
 */
export function formatPhoneMasked(phone: string | undefined | null): string {
  if (!phone) return '';
  
  // Remove tudo que não é número
  const digits = phone.replace(/\D/g, '');
  
  // Precisa ter pelo menos 4 dígitos
  if (digits.length < 4) return '****';
  
  // Pega os últimos 4 dígitos
  const ultimos4 = digits.slice(-4);
  
  return `(**) *****-${ultimos4}`;
}

/**
 * Normaliza texto para busca insensível a acentos e maiúsculas
 * Ex: "João" → "joao", "Café" → "cafe", "Açúcar" → "acucar"
 */
export function normalizeSearch(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Detecta se o dispositivo é mobile (Android, iOS) ou desktop
 * Usado para escolher entre input file com capture (mobile) ou webcam (desktop)
 */
export function isMobileDevice(): boolean {
  // Verifica user agent para dispositivos móveis conhecidos
  const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Verifica se tem touch points (dispositivos touch geralmente são mobile)
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Verifica tamanho da tela (mobile geralmente < 768px)
  const smallScreen = window.innerWidth <= 768;
  
  // Considera mobile se user agent indica OU se é tela pequena com touch
  return mobileUA || (hasTouch && smallScreen);
}
